import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { buildDataAdapter, buildPushAdapter, buildMessagingAdapter, buildFallbackBundle, DATA_ADAPTER } from '../config/adapters';
import { registerMockAdapter, unregisterMockAdapter } from '../adapters/data/mockRegistry';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';
import type { IPushAdapter } from '../adapters/push/IPushAdapter';
import type { IMessagingAdapter } from '../adapters/messaging/IMessagingAdapter';
import { useTranslation } from '../i18n/index';
import { DEFAULT_LANGUAGE } from '../config/app';
import type { Language } from '../types';

export type DataBundle = {
  adapter: IDataAdapter;
  pushAdapter: IPushAdapter;
  messagingAdapter: IMessagingAdapter;
};

/** Prefer showing real progress; avoid a long artificial floor on slow links. */
export const CONTEXT_SWITCH_MIN_MS = 80;

const DataCtx = createContext<DataBundle | null>(null);

function DataLoadingShell({ message }: { message: string }) {
  return (
    <div className="app-root data-loading" role="status" aria-live="polite">
      <div className="data-loading__spinner" aria-hidden="true" />
      <p className="data-loading__label">{message}</p>
    </div>
  );
}

export function DataProvider({
  children,
  houseId = '',
  lang = DEFAULT_LANGUAGE,
  /** Captured on mount — true when remounting after a house-scope change. */
  contextSwitch = false,
}: {
  children: ReactNode;
  houseId?: string;
  lang?: Language;
  contextSwitch?: boolean;
}) {
  const [bundle, setBundle] = useState<DataBundle | null>(null);
  const [loadError, setLoadError] = useState(false);
  const t = useTranslation(lang);
  // Freeze mount-time intent so a later parent re-render does not restart the load.
  const [isContextSwitch] = useState(contextSwitch);
  const loadingMessage = isContextSwitch ? t.updatingHouseContext : t.loadingWorkspace;

  useEffect(() => {
    let cancelled = false;
    setBundle(null);
    setLoadError(false);
    const startedAt = Date.now();

    const settle = async () => {
      if (!isContextSwitch) return;
      const wait = Math.max(0, CONTEXT_SWITCH_MIN_MS - (Date.now() - startedAt));
      if (wait > 0) {
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, wait);
        });
      }
    };

    const applyBundle = async (
      adapter: IDataAdapter,
      pushAdapter: IPushAdapter,
      messagingAdapter: IMessagingAdapter,
    ) => {
      await settle();
      if (cancelled) return;
      setLoadError(false);
      setBundle({ adapter, pushAdapter, messagingAdapter });
    };

    buildDataAdapter(houseId)
      .then(async (adapter) => {
        const messagingAdapter = await buildMessagingAdapter();
        await applyBundle(adapter as unknown as IDataAdapter, buildPushAdapter(), messagingAdapter);
      })
      .catch(async (err: unknown) => {
        console.error('[DataProvider] Failed to load data adapter:', err);
        if (import.meta.env.PROD || DATA_ADAPTER === 'firebase') {
          await settle();
          if (!cancelled) {
            setBundle(null);
            setLoadError(true);
          }
          return;
        }
        try {
          const { adapter, pushAdapter, messagingAdapter } = await buildFallbackBundle();
          await applyBundle(adapter as unknown as IDataAdapter, pushAdapter, messagingAdapter);
        } catch (fallbackErr: unknown) {
          console.error('[DataProvider] Fallback adapter failed:', fallbackErr);
          await settle();
          if (!cancelled) setLoadError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [houseId, isContextSwitch]);

  useEffect(() => {
    if (!bundle?.adapter || DATA_ADAPTER !== 'mock') return undefined;
    registerMockAdapter(bundle.adapter);
    return () => unregisterMockAdapter(bundle.adapter);
  }, [bundle?.adapter]);

  if (loadError) {
    return (
      <div className="app-root data-loading" role="alert">
        <p className="data-loading__label">{t.workspaceLoadError}</p>
      </div>
    );
  }

  if (!bundle) return <DataLoadingShell message={loadingMessage} />;

  return (
    <DataCtx.Provider value={bundle}>
      {children}
    </DataCtx.Provider>
  );
}

export const useData = (): DataBundle => {
  const ctx = useContext(DataCtx);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
};
